import { useEffect, useState } from "react";
import { toast } from "sonner";
import { adminApi, apiError } from "@/lib/api";
import { ADMIN } from "@/constants/testIds";
import { fmtMoney, fmtNum, Panel, PageHeader, Th, Td, Empty, StatusPill } from "@/components/admin/parts";
import { ConfirmDialog } from "@/components/admin/ConfirmDialog";

const TABS = [
  { key: "ops", label: "Operacje AI" },
  { key: "packs", label: "Pakiety kredytów" },
];

export default function Pricing() {
  const [tab, setTab] = useState("ops");
  const [ops, setOps] = useState(null);
  const [packs, setPacks] = useState(null);
  const [opEdit, setOpEdit] = useState({});
  const [packEdit, setPackEdit] = useState({});
  const [confirm, setConfirm] = useState(null); // {kind, id, payload, desc}
  const [loading, setLoading] = useState(false);

  const loadOps = () => adminApi.actionPrices().then((d) => setOps(d.items)).catch(() => {});
  const loadPacks = () => adminApi.creditPacks().then((d) => setPacks(d.items)).catch(() => {});
  useEffect(() => { loadOps(); loadPacks(); }, []);

  const setOpField = (k, f, v) => setOpEdit((e) => ({ ...e, [k]: { ...e[k], [f]: v } }));
  const setPackField = (id, f, v) => setPackEdit((e) => ({ ...e, [id]: { ...e[id], [f]: v } }));

  const saveOp = (key) => {
    const patch = opEdit[key];
    if (!patch) return;
    setConfirm({ kind: "op", id: key, payload: patch, desc: `Zmieniasz konfigurację akcji „${key}".` });
  };
  const savePack = (id) => {
    const patch = packEdit[id];
    if (!patch) return;
    const p = packs.find((x) => x.id === id);
    const priceChanged = patch.gross_price != null && patch.gross_price !== p.gross_price;
    const creditsChanged = patch.credits_total != null && patch.credits_total !== p.credits_total;
    const needsConfirm = priceChanged || creditsChanged;
    if (!needsConfirm) { commitPack(id, patch); return; }
    setConfirm({ kind: "pack", id, payload: patch, desc: `Zmieniasz cenę/liczbę kredytów pakietu „${p.name}". Obowiązuje tylko dla nowych sesji płatności.` });
  };

  const commitOp = async (key, patch) => {
    await adminApi.updateActionPrice(key, patch);
    toast.success("Zapisano konfigurację akcji.");
    setOpEdit((e) => ({ ...e, [key]: undefined })); loadOps();
  };
  const commitPack = async (id, patch) => {
    await adminApi.updateCreditPack(id, patch);
    toast.success("Zapisano pakiet.");
    setPackEdit((e) => ({ ...e, [id]: undefined })); loadPacks();
  };

  const runConfirm = async () => {
    setLoading(true);
    try {
      if (confirm.kind === "op") await commitOp(confirm.id, confirm.payload);
      else await commitPack(confirm.id, confirm.payload);
      setConfirm(null);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div>
      <PageHeader title="Ceny i modele" subtitle="Koszt operacji AI i pakiety kredytów" />
      <div className="mb-4 flex flex-wrap gap-2">
        {TABS.map((t) => (
          <button key={t.key} data-testid={`${ADMIN.pricingTab}-${t.key}`} onClick={() => setTab(t.key)} className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${tab === t.key ? "bg-brand-accent text-brand-on" : "border border-brand-line text-brand-sec hover:text-brand-text"}`}>{t.label}</button>
        ))}
      </div>

      {tab === "ops" && (
        <Panel testId={ADMIN.pricingTable}>
          {!ops ? <Empty>Ładowanie…</Empty> : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[820px]">
                <thead><tr className="border-b border-brand-line"><Th>Akcja</Th><Th className="text-right">Koszt (kr.)</Th><Th>Model</Th><Th>Provider</Th><Th>Rozliczana</Th><Th>Aktywna</Th><Th></Th></tr></thead>
                <tbody>
                  {ops.map((a) => {
                    const e = opEdit[a.action_key] || {};
                    const cost = e.credit_cost ?? a.credit_cost;
                    const model = e.model ?? (a.model || "");
                    const provider = e.provider ?? (a.provider || "");
                    const active = e.active ?? a.active;
                    return (
                      <tr key={a.action_key} className="border-b border-brand-line/60">
                        <Td><div className="font-medium text-brand-text">{a.label || a.action_key}</div><div className="text-xs text-brand-muted">{a.action_key}</div></Td>
                        <Td className="text-right"><input type="number" value={cost} onChange={(ev) => setOpField(a.action_key, "credit_cost", parseInt(ev.target.value, 10))} className="h-9 w-20 rounded-field border border-brand-line bg-brand-surface px-2 text-right text-sm text-brand-text outline-none" /></Td>
                        <Td><input value={model} onChange={(ev) => setOpField(a.action_key, "model", ev.target.value)} placeholder="—" className="h-9 w-32 rounded-field border border-brand-line bg-brand-surface px-2 text-sm text-brand-text outline-none" /></Td>
                        <Td><input value={provider} onChange={(ev) => setOpField(a.action_key, "provider", ev.target.value)} placeholder="—" className="h-9 w-24 rounded-field border border-brand-line bg-brand-surface px-2 text-sm text-brand-text outline-none" /></Td>
                        <Td>{a.billable ? <StatusPill status="settled" label="TAK" /> : <span className="text-xs text-brand-muted">nie</span>}</Td>
                        <Td><button onClick={() => setOpField(a.action_key, "active", !active)} className={`rounded-full px-2.5 py-1 text-xs font-semibold ${active ? "bg-brand-accent/15 text-brand-accent" : "bg-brand-elevated text-brand-muted"}`}>{active ? "Tak" : "Nie"}</button></Td>
                        <Td><button data-testid={ADMIN.pricingSave} onClick={() => saveOp(a.action_key)} disabled={!opEdit[a.action_key]} className="rounded-field bg-brand-accent px-3 py-1.5 text-sm font-semibold text-brand-on disabled:opacity-40">Zapisz</button></Td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <p className="mt-4 text-xs text-brand-muted">Zmiana ceny/modelu obowiązuje od następnej operacji. Nie zmienia historii wcześniejszych operacji.</p>
        </Panel>
      )}

      {tab === "packs" && (
        <Panel testId={ADMIN.packsTable}>
          {!packs ? <Empty>Ładowanie…</Empty> : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[900px]">
                <thead><tr className="border-b border-brand-line"><Th>Pakiet</Th><Th className="text-right">Kredyty</Th><Th className="text-right">Cena brutto</Th><Th className="text-right">Cena/kredyt</Th><Th className="text-right">Kolejność</Th><Th>Najpopularniejszy</Th><Th>Aktywny</Th><Th></Th></tr></thead>
                <tbody>
                  {packs.map((p) => {
                    const e = packEdit[p.id] || {};
                    const price = e.gross_price ?? p.gross_price;
                    const credits = e.credits_total ?? p.credits_total;
                    const order = e.display_order ?? p.display_order;
                    const active = e.active ?? p.active;
                    const badge = e.badge ?? p.badge;
                    const popular = !!badge;
                    const perCredit = credits ? (price / credits) : null;
                    return (
                      <tr key={p.id} className="border-b border-brand-line/60">
                        <Td><div className="font-medium text-brand-text">{p.name}</div><div className="text-xs text-brand-muted">{p.id}</div></Td>
                        <Td className="text-right"><input type="number" value={credits} onChange={(ev) => setPackField(p.id, "credits_total", parseInt(ev.target.value, 10))} className="h-9 w-20 rounded-field border border-brand-line bg-brand-surface px-2 text-right text-sm text-brand-text outline-none" /></Td>
                        <Td className="text-right"><input type="number" step="0.01" value={price} onChange={(ev) => setPackField(p.id, "gross_price", parseFloat(ev.target.value))} className="h-9 w-24 rounded-field border border-brand-line bg-brand-surface px-2 text-right text-sm text-brand-text outline-none" /></Td>
                        <Td className="text-right text-brand-sec">{perCredit != null ? `${perCredit.toFixed(2)} zł` : "—"}</Td>
                        <Td className="text-right"><input type="number" value={order} onChange={(ev) => setPackField(p.id, "display_order", parseInt(ev.target.value, 10))} className="h-9 w-16 rounded-field border border-brand-line bg-brand-surface px-2 text-right text-sm text-brand-text outline-none" /></Td>
                        <Td><button onClick={() => setPackField(p.id, "badge", popular ? "" : "Najpopularniejszy")} className={`rounded-full px-2.5 py-1 text-xs font-semibold ${popular ? "bg-brand-accent/15 text-brand-accent" : "bg-brand-elevated text-brand-muted"}`}>{popular ? "Tak" : "Nie"}</button></Td>
                        <Td><button onClick={() => setPackField(p.id, "active", !active)} className={`rounded-full px-2.5 py-1 text-xs font-semibold ${active ? "bg-brand-accent/15 text-brand-accent" : "bg-brand-elevated text-brand-muted"}`}>{active ? "Tak" : "Nie"}</button></Td>
                        <Td><button data-testid={ADMIN.packsSave} onClick={() => savePack(p.id)} disabled={!packEdit[p.id]} className="rounded-field bg-brand-accent px-3 py-1.5 text-sm font-semibold text-brand-on disabled:opacity-40">Zapisz</button></Td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <p className="mt-4 text-xs text-brand-muted">Nowa konfiguracja obowiązuje wyłącznie dla kolejnych sesji płatności. Nie zmienia parametrów opłaconych transakcji ani historii użytkowników.</p>
        </Panel>
      )}

      <ConfirmDialog
        open={!!confirm}
        onOpenChange={(o) => !o && setConfirm(null)}
        title="Potwierdź zmianę konfiguracji"
        description={confirm?.desc}
        confirmLabel="Zapisz zmianę"
        onConfirm={runConfirm}
        loading={loading}
        testId="admin-pricing-confirm"
      />
    </div>
  );
}
